import React, { useMemo } from 'react';
import { Icon, Skeleton } from '@innovationnorway/cl-react';
import useOpportunityReferrals from '../../../hooks/use-opportunity-referrals';
import { useParams } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import { useTranslation } from 'react-i18next';
import ReadMore from 'src/components/ReadMore/ReadMore';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import InfoBox from './InfoBox';

interface Referral {
  /** Undefined for Innovasjon Norge, which is translated when displayed */
  actorName?: string;
  serviceNames: string[];
}

const normalizeActorName = (actorName: string) => actorName.trim().toLowerCase();

// Normalized name the API always returns for Innovasjon Norge
const innovationNorway = 'innovasjon norge';

const ReferredFrom = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { getMunicipalityNameByNumber } = useGeonorge();
  const { formId } = useParams<PageParams>();
  const { opportunityReferralsQuery } = useOpportunityReferrals(formId);

  const referrals = useMemo<Referral[]>(
    () =>
      (opportunityReferralsQuery.data ?? []).map((referral) => {
        const serviceNames = referral.recommendedServices
          .map((service) => service.navn)
          .filter((name): name is string => !!name);

        const municipalityCode = referral.forwardedFromMunicipalityCode;
        if (municipalityCode) {
          serviceNames.push(
            tOneWayIn('opportunityDetails.FirstLineServiceFor', {
              municipality: getMunicipalityNameByNumber(municipalityCode) ?? municipalityCode,
            }),
          );
        }

        const isInnovationNorway = normalizeActorName(referral.referrerName) === innovationNorway;
        return {
          actorName: isInnovationNorway ? undefined : referral.referrerName,
          serviceNames,
        };
      }),
    [getMunicipalityNameByNumber, opportunityReferralsQuery.data, tOneWayIn],
  );

  const renderContent = () => {
    if (opportunityReferralsQuery.isLoading) {
      return (
        <div className="display--flex flex-direction--column gap--ds-2">
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      );
    }

    return (
      <ReadMore maxHeight="var(--opportunity-overview-collapsed-height)">
        <ul className="opportunity-overview--referral-list display--flex flex-direction--column">
          {referrals.map((referral) => (
            <ReferralRow
              key={referral.actorName ?? innovationNorway}
              actorName={referral.actorName ?? tCommon('innovationNorway')}
              serviceNames={referral.serviceNames}
            />
          ))}
        </ul>
      </ReadMore>
    );
  };

  return (
    <InfoBox heading={tOneWayIn('opportunityDetails.ActorsWhoReferredToYou')}>{renderContent()}</InfoBox>
  );
};

const ReferralRow = ({ actorName, serviceNames }: { actorName: string; serviceNames: string[] }) => {
  return (
    <li className="opportunity-overview--referral-row display--flex flex-direction--column">
      <div className="display--flex flex-direction--row align-items--center justify-content--space-between gap--1">
        {actorName}
        <Icon prefix="fasr" iconName={serviceNames.length > 0 ? 'hand-holding-heart' : 'paper-plane'} />
      </div>
      {serviceNames.length > 0 && (
        <ul className="opportunity-overview--referral-list display--flex flex-direction--column gap--ds-1">
          {serviceNames.map((serviceName) => (
            <li key={serviceName} className="opportunity-overview--referral-service font-weight--bold">
              {serviceName}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default ReferredFrom;
